import Phaser from 'phaser';
import { HAPTICS, PALETTE } from '../../config';
import { createAthleteView, type AthleteView } from '../../game/AthleteView';
import { AudioEngine } from '../../game/AudioEngine';
import { getDiscipline } from '../../game/data/disciplines';
import { getSkin } from '../../game/data/skins';
import { pickOpponents } from '../../game/data/aiRoster';
import { t } from '../../game/i18n';
import type { DisciplineResult, Standing } from '../../game/types';
import { Button } from '../../ui/Button';
import { PowerMeter } from '../../ui/PowerMeter';
import { TapZones } from '../../ui/TapZones';
import { viewport } from '../../ui/viewport';
import { DisciplineScene } from './DisciplineScene';

type Side = 'L' | 'R';
type Phase = 'intro' | 'lift' | 'raise' | 'hold' | 'done';

const BASE_KG = 70;
const HOLD_TIME = 2.6; // seconds to keep the bar balanced
const LO = 0.22;
const HI = 0.78;

/**
 * Weightlifting — two phases. First MASH L/R to charge power, then tap SOLLEVA to attempt a
 * weight (more power = heavier bar). Then HOLD it overhead: a balance needle drifts, tap the
 * opposite side to keep it centred for a couple of seconds. Hold it = the kg count; drop = 0.
 */
export class Weightlifting extends DisciplineScene {
  private phase: Phase = 'intro';
  private rig!: AthleteView;

  private power = 0;
  private lastSide: Side | null = null;
  private rangeKg = 180;
  private weight = 0;
  private balance = 0.5;
  private drift = 0;
  private held = 0;
  private raiseT = 0;
  private failed = false;

  private W = 0; private H = 0; private groundY = 0; private uiS = 1;
  private aiLifts: { name: string; species: string; nationality: string; metric: number }[] = [];

  private powerBar!: PowerMeter;
  private balanceBar!: PowerMeter;
  private hud!: Phaser.GameObjects.Text;
  private bigText!: Phaser.GameObjects.Text;
  private actBtn!: Button;
  private tapZones!: TapZones;
  private bar!: Phaser.GameObjects.Graphics;

  constructor() { super('Weightlifting'); }

  create(): void {
    super.create();
    this.phase = 'intro';
    this.power = 0; this.lastSide = null; this.weight = 0; this.balance = 0.5; this.drift = 0; this.held = 0; this.raiseT = 0; this.failed = false;

    const { w, h, s } = viewport(this);
    this.W = w; this.H = h; this.uiS = s;
    this.groundY = h - Math.max(96, h * 0.2);
    this.rangeKg = 150 + (this.athlete.stats.power / 100) * 120; // up to ~270kg potential

    const src = this.textures.get('stadium').getSourceImage();
    this.add.image(0, 0, 'stadium').setOrigin(0, 0).setScrollFactor(0).setDepth(-10)
      .setScale(Math.max(w / src.width, (this.groundY + 30) / src.height));
    this.add.graphics().setDepth(-6).fillStyle(0x6a5436, 1).fillRect(0, this.groundY, w, h - this.groundY); // platform
    this.rig = createAthleteView(this, w * 0.5, this.groundY, this.athlete, Phaser.Math.Clamp(h * 0.4, 190, 360));
    this.rig.container.setDepth(6);
    this.bar = this.add.graphics().setDepth(7);

    const opponents = pickOpponents(getSkin(this.athlete.skin).id, 3);
    this.aiLifts = opponents.map((o) => ({ name: o.name, species: o.species, nationality: o.nationality, metric: Math.round(Math.max(80, 120 + o.jumpDistance * 12 + (Math.random() * 40 - 20))) }));

    this.buildHud();
    this.controls.on('tapLeft', () => this.onTap('L'));
    this.controls.on('tapRight', () => this.onTap('R'));
    this.controls.on('action', () => this.onAction());
    this.startCountdown();
  }

  private buildHud(): void {
    const fs = (n: number) => `${Math.max(13, Math.round(n * this.uiS))}px`;
    this.hud = this.add.text(this.W / 2, 16, t('game.hudMash'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(24), color: '#f4f7ff', fontStyle: 'bold' })
      .setOrigin(0.5, 0).setScrollFactor(0).setShadow(0, 3, '#000000aa', 5);
    const barW = Math.min(300, this.W * 0.5);
    const topY = Math.max(50, this.H * 0.13);
    this.powerBar = new PowerMeter(this, this.W / 2, topY, { width: barW, height: 18, fillColor: PALETTE.power });
    this.add.text(this.W / 2, topY - 18, t('hud.power'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(12), color: '#d3dbf2' }).setOrigin(0.5).setScrollFactor(0);
    // Balance meter: centred sweet-spot band; the needle is the bar's tilt.
    this.balanceBar = new PowerMeter(this, this.W / 2, topY, { width: barW, height: 18, target: [LO, HI], fillColor: PALETTE.cadence });
    this.balanceBar.setVisible(false);
    this.bigText = this.add.text(this.W / 2, this.H * 0.38, '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(72), color: '#ffd24a', fontStyle: 'bold' })
      .setOrigin(0.5).setScrollFactor(0).setShadow(0, 8, '#00000088', 12);
    const label = this.sys.game.device.os.desktop ? `🏋️ ${t('btn.lift')} ${t('btn.space')}` : `🏋️ ${t('btn.lift')}`;
    this.actBtn = new Button(this, this.W / 2, this.H - Math.max(30, 38 * this.uiS), label, () => this.onAction(), {
      width: Math.min(210, this.W * 0.42), height: Math.max(52, 58 * this.uiS), fontSize: Math.max(18, Math.round(22 * this.uiS)), color: PALETTE.power,
    });
    this.actBtn.setScrollFactor(0).setAlpha(0.92).setVisible(false);
    this.tapZones = new TapZones(this);
    this.tapZones.setVisible(false);
  }

  private startCountdown(): void {
    ['3', '2', '1', t('game.go')].forEach((label, i) => {
      this.time.delayedCall(i * 600, () => {
        this.bigText.setText(label).setAlpha(1).setScale(1.5);
        this.tweens.add({ targets: this.bigText, scale: 1, duration: 300, ease: 'Back.out' });
        if (label === t('game.go')) {
          AudioEngine.go();
          this.phase = 'lift';
          this.controls.setEnabled(true);
          this.actBtn.setVisible(true);
          this.powerBar.setVisible(true);
          this.tapZones.setVisible(true);
          this.drawBar(this.groundY - 10, 0);
          this.tweens.add({ targets: this.bigText, alpha: 0, delay: 250, duration: 200 });
        } else AudioEngine.countdownTick();
      });
    });
  }

  private onTap(side: Side): void {
    if (this.phase === 'lift') {
      const alternated = side !== this.lastSide;
      this.lastSide = side;
      this.power = Math.min(1, this.power + (alternated ? 0.06 : 0.02));
      this.tapZones.flash(side);
      AudioEngine.tap(alternated ? 0.6 : 0.2);
      this.vibrate(HAPTICS.tap);
    } else if (this.phase === 'hold') {
      // Nudge the bar back toward centre (tap the side it's leaning AWAY from).
      this.balance += side === 'L' ? -0.07 : 0.07;
      this.drift *= 0.5;
      this.tapZones.flash(side);
      AudioEngine.tap(0.4);
    }
  }

  private onAction(): void {
    if (this.phase !== 'lift') return;
    this.weight = Math.round(BASE_KG + this.power * this.rangeKg);
    this.phase = 'raise';
    this.raiseT = 0;
    this.actBtn.setVisible(false);
    this.powerBar.setVisible(false);
    this.tapZones.setVisible(false);
    this.controls.setEnabled(false);
    this.rig.setPose('celebrate');
    AudioEngine.whoosh();
    this.hud.setText(t('game.weightHold1', { kg: this.weight }));
  }

  update(_t: number, delta: number): void {
    const dt = delta / 1000;
    this.rig.update(dt);
    if (this.phase === 'lift') {
      this.power = Math.max(0, this.power - dt * 0.18); // bleeds without taps
      this.powerBar.set(this.power);
    } else if (this.phase === 'raise') {
      this.raiseT += dt;
      const p = Phaser.Math.Clamp(this.raiseT / 0.6, 0, 1);
      this.drawBar(Phaser.Math.Linear(this.groundY - 10, this.overheadY(), p), 0);
      if (p >= 1) { this.phase = 'hold'; this.balanceBar.setVisible(true); this.controls.setEnabled(true); this.tapZones.setVisible(true); }
    } else if (this.phase === 'hold') {
      this.updateHold(dt);
    }
  }

  private overheadY(): number {
    return this.groundY - Phaser.Math.Clamp(this.H * 0.42, 190, 360) * 0.92;
  }

  private updateHold(dt: number): void {
    // Heavier weight drifts harder; random walk biased away from centre.
    const heavy = (this.weight - BASE_KG) / this.rangeKg; // 0..1
    this.drift += (Math.random() - 0.5) * (0.9 + heavy * 1.8) * dt;
    this.drift = Phaser.Math.Clamp(this.drift, -0.5, 0.5);
    this.balance += this.drift * dt;
    this.balanceBar.set(this.balance, this.balance);
    // Tilt the bar + athlete with the balance.
    const tilt = (this.balance - 0.5) * 0.5;
    this.drawBar(this.overheadY(), tilt);
    this.rig.container.setAngle(tilt * 18);
    if (this.balance < LO || this.balance > HI) { this.failed = true; this.finishLift(); return; }
    this.held += dt;
    this.hud.setText(t('game.weightHold2', { kg: this.weight, s: Math.max(0, HOLD_TIME - this.held).toFixed(1) }));
    if (this.held >= HOLD_TIME) this.finishLift();
  }

  private drawBar(y: number, tilt: number): void {
    const g = this.bar;
    g.clear();
    const cx = this.rig.container.x;
    const half = 70;
    const dx = Math.cos(tilt) * half, dy = Math.sin(tilt) * half;
    g.lineStyle(6, 0xcfd6e6, 1).beginPath();
    g.moveTo(cx - dx, y - dy); g.lineTo(cx + dx, y + dy); g.strokePath();
    // Plates at each end.
    for (const s of [-1, 1]) {
      const px = cx + s * dx, py = y + s * dy;
      g.fillStyle(0x2a3358, 1).fillRoundedRect(px - 7, py - 22, 14, 44, 4);
      g.fillStyle(0x59d2ff, 0.9).fillRoundedRect(px - 5, py - 18, 10, 36, 3);
    }
  }

  private finishLift(): void {
    this.phase = 'done';
    this.controls.setEnabled(false);
    this.tapZones.setVisible(false);
    this.balanceBar.setVisible(false);
    this.rig.container.setAngle(0);
    const kg = this.failed ? 0 : this.weight;
    if (this.failed) {
      this.bar.clear();
      this.cameras.main.shake(200, 0.01);
      AudioEngine.thud();
      this.rig.setPose('exhausted');
    } else {
      AudioEngine.chime();
      AudioEngine.fanfare();
    }
    this.vibrate(HAPTICS.finish);

    const standings: Standing[] = [
      { name: this.athlete.name, species: this.athlete.species, isPlayer: true, nationality: this.athlete.nationality, metric: kg },
      ...this.aiLifts.map((a) => ({ name: a.name, species: a.species, isPlayer: false, nationality: a.nationality, metric: a.metric })),
    ].sort((x, y) => y.metric - x.metric);
    const rank = standings.findIndex((s) => s.isPlayer) + 1;
    const score = Math.round(Phaser.Math.Clamp(kg * 3.6, 0, 1000));
    const reward = getDiscipline('weightlifting').scoreToReward(score);

    this.bigText.setText(this.failed ? t('game.foul') : `${kg} kg`).setColor(this.failed ? '#ff5d6c' : '#ffd24a').setAlpha(1).setScale(0.6);
    this.tweens.add({ targets: this.bigText, scale: 1, duration: 500, ease: 'Back.out' });

    const result: DisciplineResult = {
      disciplineId: 'weightlifting', metricKind: 'distance', athleteId: this.athlete.id, athleteName: this.athlete.name,
      species: this.athlete.species, nationality: this.athlete.nationality, metric: kg, score, reward, rank, standings,
    };
    this.time.delayedCall(2400, () => this.finishDiscipline(result));
  }

  private vibrate(pattern: number | number[]): void {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try { navigator.vibrate(pattern); } catch { /* unsupported */ }
    }
  }
}
